// mds-dojo — dijit/form/Select — FAITHFUL port of references/dijit/form/Select.js.
// (OVERWRITES the earlier freestyle-lite stub.) A styleable <select> replacement:
// a DropDownButton-like widget backed by _FormSelectWidget's option model, with a
// scrollable DropDownMenu of MenuItem/MenuSeparator, keyboard search (_KeyNavMixin),
// required/validation state, and lazy dropdown loading (_HasDropDown).
//
// ADAPTATIONS for this package:
//  - `dojo/text!./templates/Select.html` → `import template from './templates/Select.html?raw'`
//    (+ template copied into dijit/form/templates/).
//  - `dojo/i18n!./nls/validate` → inlined `missingMessage` (dojo English root).
//  - `dojo/sniff` → `../../dojo/has`; `dojo/_base/declare` → `../../dojo/_base/declare-runtime`;
//    all dojo/dijit ids → relative. `has("dojo-bidi")` is falsy → the bidi
//    _setDisplay/applyTextDir extension subclass is dropped (evergreen, non-bidi build).
//  - unused `dojo/dom-geometry` import dropped.
//  - FAITHFUL-ADAPTATION (flagged): mds `dijit/Tooltip` is a lite widget that lacks the
//    static Tooltip.show()/Tooltip.hide() master-tooltip API this widget's validate()
//    relies on. Since dijit/Tooltip is sibling-owned (must not edit), a minimal
//    module-local master tooltip (one shared <body> node) provides show/hide so
//    validation messages still display. It is NOT the full dijit master tooltip
//    (no fade animation, simplified positioning, no aria-describedby wiring). The core
//    validation LOGIC (state/message/aria-invalid) is fully faithful.
//  - The `_onEventAfterLoad` prototype-patch (which used the strict-mode-incompatible
//    `this.inherited(name, arguments)` from a RAW function) is expressed as ordinary
//    declared `_onContainerKeydown`/`_onContainerKeypress` methods — behaviorally
//    identical (lazy-load dropdown, then delegate to the _KeyNavMixin super) and
//    strict-ESM-correct.
import array from '../../dojo/_base/array'; // array.forEach
import declare from '../../dojo/_base/declare-runtime'; // declare
import domAttr from '../../dojo/dom-attr'; // domAttr.set
import domClass from '../../dojo/dom-class'; // domClass.add domClass.remove domClass.toggle
import keys from '../../dojo/keys';
import lang from '../../dojo/_base/lang'; // lang.hitch
import on from '../../dojo/on';
import has from '../../dojo/has'; // has("ie")
import _FormSelectWidget from './_FormSelectWidget';
import _HasDropDown from '../_HasDropDown';
import DropDownMenu from '../DropDownMenu';
import MenuItem from '../MenuItem';
import MenuSeparator from '../MenuSeparator';
import Tooltip from '../Tooltip';
import _KeyNavMixin from '../_KeyNavMixin';
import registry from '../registry'; // registry.byNode
import template from './templates/Select.html?raw';

// module:
//		dijit/form/Select

// Inlined dojo/nls/validate (root/English) — only missingMessage is consumed here.
var validateNls: any = {
	invalidMessage: "The value entered is not valid.",
	missingMessage: "This value is required.",
	rangeMessage: "This value is out of range."
};

// --- minimal module-local master tooltip (see header FAITHFUL-ADAPTATION note) ------
var _masterTT: any = null;
function _ttShow(message: string, aroundNode: any, ..._rest: any[] /*, position, rtl */) {
	if ((Tooltip as any).show) { return (Tooltip as any).show.apply(Tooltip, arguments as any); }
	if (!_masterTT && typeof document !== "undefined") {
		_masterTT = document.createElement("div");
		_masterTT.className = "dijitTooltip dijitTooltipDialog";
		_masterTT.style.cssText = "position:absolute;display:none;z-index:1000;";
		document.body.appendChild(_masterTT);
	}
	if (!_masterTT) { return; }
	_masterTT.innerHTML = message;
	_masterTT.style.display = "";
	try {
		var r = aroundNode.getBoundingClientRect();
		_masterTT.style.left = (r.left + (window.pageXOffset || 0)) + "px";
		_masterTT.style.top = (r.bottom + (window.pageYOffset || 0)) + "px";
	} catch (e) { /* squelch */ }
}
function _ttHide(aroundNode: any) {
	if ((Tooltip as any).hide) { return (Tooltip as any).hide.apply(Tooltip, arguments as any); }
	if (_masterTT) { _masterTT.style.display = "none"; _masterTT.innerHTML = ""; }
}

var _SelectMenu: any = declare("dijit.form._SelectMenu", DropDownMenu, {
	// summary:
	//		An internally-used menu for dropdown that allows us a vertical scrollbar

	// Override Menu.autoFocus setting so that opening a Select highlights the current value.
	autoFocus: true,

	buildRendering: function () {
		this.inherited(arguments);

		this.domNode.setAttribute("role", "listbox");
	},

	postCreate: function () {
		this.inherited(arguments);

		// stop mousemove from selecting text on IE to be consistent with other browsers
		this.own(on(this.domNode, "selectstart", function (evt: any) {
			evt.preventDefault();
			evt.stopPropagation();
		}));
	},

	focus: function () {
		// summary:
		//		Overridden so that the previously selected value will be focused instead of only the first item
		var found = false,
			val = this.parentWidget.value;
		if (lang.isArray(val)) {
			val = val[val.length - 1];
		}
		if (val) { // if focus selected
			array.forEach(this.parentWidget._getChildren(), function (child: any) {
				if (child.option && (val === child.option.value)) { // find menu item widget with this value
					found = true;
					this.focusChild(child, false); // focus previous selection
				}
			}, this);
		}
		if (!found) {
			this.inherited(arguments); // focus first item by default
		}
	}
});

var Select: any = declare("dijit.form.Select", [_FormSelectWidget, _HasDropDown, _KeyNavMixin], {
	// summary:
	//		This is a "styleable" select box - it is basically a DropDownButton which
	//		can take a `<select>` as its input.

	baseClass: "dijitSelect dijitValidationTextBox",

	templateString: template,

	_buttonInputDisabled: has("ie") ? "disabled" : "", // allows IE to disallow focus, but Firefox cannot be disabled for mousedown events

	// required: Boolean
	//		Can be true or false, default is false.
	required: false,

	// state: [readonly] String
	//		"Incomplete" if this select is required but unset (i.e. blank value), "" otherwise
	state: "",

	// message: String
	//		Currently displayed error/prompt message
	message: "",

	// tooltipPosition: String[]
	//		See description of `dijit/Tooltip.defaultPosition` for details on this parameter.
	tooltipPosition: [],

	// emptyLabel: string
	//		What to display in an "empty" dropdown
	emptyLabel: "&#160;", // &nbsp;

	// _isLoaded: Boolean
	//		Whether or not we have been loaded
	_isLoaded: false,

	// _childrenLoaded: Boolean
	//		Whether or not our children have been loaded
	_childrenLoaded: false,

	// labelType: String
	//		Specifies how to interpret the labelAttr in the data store items.
	//		Can be "html" or "text".
	labelType: "html",

	_fillContent: function () {
		// summary:
		//		Set the value to be the first, or the selected index
		this.inherited(arguments);
		// set value from selected option
		if (this.options.length && !this.value && this.srcNodeRef) {
			var si = this.srcNodeRef.selectedIndex || 0; // || 0 needed for when srcNodeRef is not a SELECT
			this._set("value", this.options[si >= 0 ? si : 0].value);
		}
		// Create the dropDown widget
		this.dropDown = new _SelectMenu({ id: this.id + "_menu", parentWidget: this });
		domClass.add(this.dropDown.domNode, this.baseClass.replace(/\s+|$/g, "Menu "));
	},

	_getMenuItemForOption: function (/*_FormSelectWidget.__SelectOption*/ option: any) {
		// summary:
		//		For the given option, return the menu item that should be
		//		used to display it.  This can be overridden as needed
		if (!option.value && !option.label) {
			// We are a separator (no label set for it)
			return new MenuSeparator({ ownerDocument: this.ownerDocument });
		} else {
			// Just a regular menu option
			var click = lang.hitch(this, "_setValueAttr", option);
			var item = new MenuItem({
				option: option,
				label: (this.labelType === 'text' ? (option.label || '').toString()
					.replace(/&/g, '&amp;').replace(/</g, '&lt;') :
					option.label) || this.emptyLabel,
				onClick: click,
				ownerDocument: this.ownerDocument,
				dir: this.dir,
				textDir: this.textDir,
				disabled: option.disabled || false
			});
			item.focusNode.setAttribute("role", "option");
			return item;
		}
	},

	_addOptionItem: function (/*_FormSelectWidget.__SelectOption*/ option: any) {
		// summary:
		//		For the given option, add an option to our dropdown.
		//		If the option doesn't have a value, then a separator is added
		//		in that place.
		if (this.dropDown) {
			this.dropDown.addChild(this._getMenuItemForOption(option));
		}
	},

	_getChildren: function () {
		if (!this.dropDown) {
			return [];
		}
		return this.dropDown.getChildren();
	},

	focus: function () {
		// Override _KeyNavMixin::focus(), which calls focusFirstChild().
		// We just want the standard form widget behavior.
		if (!this.disabled && this.focusNode.focus) {
			try {
				this.focusNode.focus();
			} catch (e) {
				/*squelch errors from hidden nodes*/
			}
		}
	},

	focusChild: function (/*dijit/_WidgetBase*/ widget: any) {
		// summary:
		//		Sets the value to the given option, used during search by letter.
		// tags:
		//		protected
		if (widget) {
			this.set('value', widget.option);
		}
	},

	_getFirst: function () {
		// summary:
		//		Returns the first child widget.
		// tags:
		//		abstract extension
		var children = this._getChildren();
		return children.length ? children[0] : null;
	},

	_getLast: function () {
		// summary:
		//		Returns the last child widget.
		// tags:
		//		abstract extension
		var children = this._getChildren();
		return children.length ? children[children.length - 1] : null;
	},

	childSelector: function (/*DOMNode*/ node: any) {
		// Implement _KeyNavMixin.childSelector, to identify focusable child nodes.
		var widget = registry.byNode(node);
		return widget && widget.getParent() == this.dropDown;
	},

	onKeyboardSearch: function (/*dijit/_WidgetBase*/ item: any, /*Event*/ evt: any, /*String*/ searchString: any, /*Number*/ numMatches: any) {
		// summary:
		//		When a key is pressed that matches a child item,
		//		this method is called so that a widget can take appropriate action is necessary.
		// tags:
		//		protected
		if (item) {
			this.focusChild(item);
		}
	},

	_loadChildren: function (/*Boolean*/ loadMenuItems: any) {
		// summary:
		//		Resets the menu and the length attribute of the button - and
		//		ensures that the label is appropriately set.

		if (loadMenuItems === true) {
			// this.inherited destroys this.dropDown's child widgets (MenuItems).
			if (this.dropDown) {
				delete this.dropDown.focusedChild;
				this.focusedChild = null;
			}
			if (this.options.length) {
				this.inherited(arguments);
			} else {
				// Drop down menu is blank but add one blank entry just so something appears on the screen
				array.forEach(this._getChildren(), function (child: any) {
					child.destroyRecursive();
				});
				var item = new MenuItem({
					ownerDocument: this.ownerDocument,
					label: this.emptyLabel
				});
				this.dropDown.addChild(item);
			}
		} else {
			this._updateSelection();
		}

		this._isLoaded = false;
		this._childrenLoaded = true;

		if (!this._loadingStore) {
			// Don't call this if we are loading - since we will handle it later
			this._setValueAttr(this.value, false);
		}
	},

	_refreshState: function () {
		if (this._started) {
			this.validate(this.focused);
		}
	},

	startup: function () {
		this.inherited(arguments);
		this._refreshState(); // after all _set* methods have run
	},

	_setValueAttr: function (value: any) {
		this.inherited(arguments);
		domAttr.set(this.valueNode, "value", this.get("value"));
		this._refreshState();	// to update this.state
	},

	_setNameAttr: "valueNode",

	_setDisabledAttr: function (/*Boolean*/ value: any) {
		this.inherited(arguments);
		this._refreshState();	// to update this.state
	},

	_setRequiredAttr: function (/*Boolean*/ value: any) {
		this._set("required", value);
		this.focusNode.setAttribute("aria-required", value);
		this._refreshState();	// to update this.state
	},

	_setOptionsAttr: function (/*Array*/ options: any) {
		this._isLoaded = false;
		this._set('options', options);
	},

	_setDisplay: function (/*String*/ newDisplay: any) {
		// summary:
		//		sets the display for the given value (or values)

		var lbl = (this.labelType === 'text' ? (newDisplay || '')
			.replace(/&/g, '&amp;').replace(/</g, '&lt;') :
			newDisplay) || this.emptyLabel;
		this.containerNode.innerHTML = '<span role="option" aria-selected="true" class="dijitReset dijitInline ' + this.baseClass.replace(/\s+|$/g, "Label ") + '">' + lbl + '</span>';
	},

	validate: function (/*Boolean*/ isFocused: any) {
		// summary:
		//		Called by oninit, onblur, and onkeypress, and whenever required/disabled state changes
		// description:
		//		Show missing or invalid messages if appropriate, and highlight textbox field.

		var isValid = this.disabled || this.isValid(isFocused);
		this._set("state", isValid ? "" : (this._hasBeenBlurred ? "Error" : "Incomplete"));
		this.focusNode.setAttribute("aria-invalid", isValid ? "false" : "true");
		var message = isValid ? "" : this._missingMsg;
		if (message && this.focused && this._hasBeenBlurred) {
			_ttShow(message, this.domNode, this.tooltipPosition, !this.isLeftToRight());
		} else {
			_ttHide(this.domNode);
		}
		this._set("message", message);
		return isValid;
	},

	isValid: function (/*Boolean*/ /*===== isFocused =====*/) {
		// summary:
		//		Whether or not this is a valid value.  The only way a Select
		//		can be invalid is when it's required but nothing is selected.
		return (!this.required || this.value === 0 || !(/^\s*$/.test(this.value || ""))); // handle value is null or undefined
	},

	reset: function () {
		// summary:
		//		Overridden so that the state will be cleared.
		this.inherited(arguments);
		_ttHide(this.domNode);
		this._refreshState();	// to update this.state
	},

	postMixInProperties: function () {
		// summary:
		//		set the missing message
		this.inherited(arguments);
		this._missingMsg = validateNls.missingMessage;
	},

	postCreate: function () {
		this.inherited(arguments);

		// stop mousemove from selecting text on IE to be consistent with other browsers
		this.own(on(this.domNode, "selectstart", function (evt: any) {
			evt.preventDefault();
			evt.stopPropagation();
		}));

		this.domNode.setAttribute("aria-expanded", "false");

		// Prevent _KeyNavMixin from calling stopPropagation() on left and right arrow keys, thus breaking
		// navigation when Select inside Toolbar.
		var keyNavCodes = this._keyNavCodes;
		delete keyNavCodes[keys.LEFT_ARROW];
		delete keyNavCodes[keys.RIGHT_ARROW];
	},

	_setStyleAttr: function (/*String||Object*/ value: any) {
		this.inherited(arguments);
		domClass.toggle(this.domNode, this.baseClass.replace(/\s+|$/g, "FixedWidth "), !!this.domNode.style.width);
	},

	isLoaded: function () {
		return this._isLoaded;
	},

	loadDropDown: function (/*Function*/ loadCallback: any) {
		// summary:
		//		populates the menu
		this._loadChildren(true);
		this._isLoaded = true;
		loadCallback();
	},

	destroy: function (preserveDom: any) {
		if (this.dropDown && !this.dropDown._destroyed) {
			this.dropDown.destroyRecursive(preserveDom);
			delete this.dropDown;
		}
		_ttHide(this.domNode);	// in case Select (or enclosing Dialog) destroyed while tooltip shown
		this.inherited(arguments);
	},

	_onFocus: function () {
		this.validate(true);	// show tooltip if second focus of required tooltip, but no selection
		// Note: not calling superclass _onFocus() to avoid _KeyNavMixin::_onFocus() setting tabIndex --> -1
	},

	_onBlur: function () {
		_ttHide(this.domNode);
		this.inherited(arguments);
		this.validate(false);
	},

	// generic lazy-load-before-dispatch wrappers for the _KeyNavMixin container key
	// handlers (see header note: replaces the source's post-declare prototype patch).
	_onContainerKeydown: function (evt: any) {
		if (!this._isLoaded) {
			this.loadDropDown(lang.hitch(this, "_onContainerKeydown", evt));
		} else {
			this.inherited(arguments);
		}
	},

	_onContainerKeypress: function (evt: any) {
		if (!this._isLoaded) {
			this.loadDropDown(lang.hitch(this, "_onContainerKeypress", evt));
		} else {
			this.inherited(arguments);
		}
	}
});

Select._Menu = _SelectMenu;	// for monkey patching

export default Select;
